import React, { useState, useMemo, useEffect } from "react";
import {
    Box, Typography, Card, CardContent, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, Select, MenuItem, IconButton,
    TextField, InputAdornment, Skeleton, Chip,
} from "@mui/material";
import KeyboardArrowLeft      from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRight     from "@mui/icons-material/KeyboardArrowRight";
import SearchIcon             from "@mui/icons-material/Search";
import CloseIcon              from "@mui/icons-material/Close";
import CurrencyRupeeRoundedIcon from "@mui/icons-material/CurrencyRupeeRounded";
import AccountBalanceWalletRoundedIcon from "@mui/icons-material/AccountBalanceWalletRounded";
import PointOfSaleRoundedIcon from "@mui/icons-material/PointOfSaleRounded";
import TrendingDownRoundedIcon from "@mui/icons-material/TrendingDownRounded";
import TrendingUpRoundedIcon  from "@mui/icons-material/TrendingUpRounded";
import { getData, API_ENDPOINTS } from "../../services/apiService.js";

// ── Brand palette ─────────────────────────────────────────────────────────
const T = {
    teal:    "#007C84", tealDk: "#016268", tealLt: "#E0F4F5",
    orange:  "#FF7900", orangeLt: "#FFF0E0",
    emerald: "#059669", emeraldLt: "#D1FAE5",
    rose:    "#E11D48", roseLt:   "#FFE4E6",
    indigo:  "#4F46E5", indigoLt: "#EEF2FF",
    amber:   "#D97706", amberLt:  "#FEF3C7",
    bg:      "#F7FAFC", border: "#E2E8F0",
    text:    "#0F172A", muted:  "#64748B",
};

// ── Helpers ───────────────────────────────────────────────────────────────
const fmtRs  = n => { const v = parseFloat(n); return Number.isFinite(v) ? `₹${v.toLocaleString("en-IN", { minimumFractionDigits: 2 })}` : "—"; };
const fmtK   = n => { const v = Number(n); if (!v) return "₹0"; if (v >= 100000) return `₹${(v/100000).toFixed(1)}L`; if (v >= 1000) return `₹${(v/1000).toFixed(1)}k`; return `₹${v.toFixed(0)}`; };
const fmtDate= d => new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });

// ── KPI tile ──────────────────────────────────────────────────────────────
const KpiTile = ({ title, value, icon, accent, iconBg, iconColor, loading }) => (
    <Card sx={{
        flex: "1 1 160px", borderRadius: "16px", border: `1px solid ${T.border}`,
        boxShadow: "0 1px 4px rgba(15,23,42,0.06)", overflow: "hidden",
    }}>
        <Box sx={{ height: 3, background: `linear-gradient(90deg, ${accent}, ${accent}66)` }} />
        <CardContent sx={{ p: 2.5, "&:last-child": { pb: 2.5 } }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                <Box>
                    <Typography sx={{ fontSize: 11, fontWeight: 700, color: T.muted, textTransform: "uppercase", letterSpacing: "0.4px", mb: 0.8 }}>{title}</Typography>
                    {loading
                        ? <Skeleton width={90} height={32} />
                        : <Typography sx={{ fontSize: 24, fontWeight: 800, color: accent, lineHeight: 1.1, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{value}</Typography>
                    }
                </Box>
                <Box sx={{ width: 42, height: 42, borderRadius: "12px", bgcolor: iconBg, display: "flex", alignItems: "center", justifyContent: "center" }}>
                    {React.cloneElement(icon, { sx: { fontSize: 20, color: iconColor } })}
                </Box>
            </Box>
        </CardContent>
    </Card>
);

// ── Type chip ─────────────────────────────────────────────────────────────
const TypeChip = ({ type }) => {
    const isCredit = type?.toLowerCase() === "credit";
    return (
        <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, px: 1.2, py: 0.3, borderRadius: "8px", bgcolor: isCredit ? T.emeraldLt : T.roseLt, border: `1px solid ${isCredit ? T.emerald : T.rose}22` }}>
            <Box sx={{ width: 5, height: 5, borderRadius: "50%", bgcolor: isCredit ? T.emerald : T.rose }} />
            <Typography sx={{ fontSize: 11, fontWeight: 700, color: isCredit ? T.emerald : T.rose, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{type}</Typography>
        </Box>
    );
};

// ── Method badge ──────────────────────────────────────────────────────────
const MethodBadge = ({ method }) => {
    if (!method || method === "—") return <Typography sx={{ fontSize: 12, color: T.muted }}>—</Typography>;
    const METHOD_COLORS = { wallet: [T.tealLt, T.teal], neft: [T.indigoLt, T.indigo], upi: [T.amberLt, T.amber], cod: [T.orangeLt, T.orange] };
    const [bg, fg] = METHOD_COLORS[method.toLowerCase()] || [T.bg, T.muted];
    return (
        <Box sx={{ display: "inline-flex", alignItems: "center", px: 1.2, py: 0.3, bgcolor: bg, borderRadius: "7px", border: `1px solid ${fg}22` }}>
            <Typography sx={{ fontSize: 11, fontWeight: 700, color: fg, textTransform: "uppercase", letterSpacing: "0.3px" }}>{method}</Typography>
        </Box>
    );
};

// ── Filter pill ───────────────────────────────────────────────────────────
const FilterPill = ({ label, active, color, bg, onClick }) => (
    <Box onClick={onClick} sx={{
        px: 1.6, py: 0.6, borderRadius: "8px", cursor: "pointer",
        bgcolor: active ? bg : "transparent", color: active ? color : T.muted,
        fontWeight: active ? 700 : 500, fontSize: 12.5,
        border: `1.5px solid ${active ? color + "44" : T.border}`,
        transition: "all 0.14s", whiteSpace: "nowrap",
        "&:hover": { bgcolor: bg, color },
        fontFamily: "'Plus Jakarta Sans', sans-serif",
    }}>{label}</Box>
);

// ── Skeleton row ──────────────────────────────────────────────────────────
const SkelRow = () => (
    <TableRow>
        {[80, 140, 90, 90, 70, 90, 80, 80].map((w, i) => <TableCell key={i}><Skeleton width={w} height={16} /></TableCell>)}
    </TableRow>
);

// ── Main component ────────────────────────────────────────────────────────
export default function Payments() {
    const [payments,   setPayments]   = useState([]);
    const [loading,    setLoading]    = useState(false);
    const [summary,    setSummary]    = useState(null);
    const [search,     setSearch]     = useState("");
    const [typeFilter, setTypeFilter] = useState("all");
    const [page,       setPage]       = useState(0);
    const [perPage,    setPerPage]    = useState(15);

    const fetchPayments = async (q = "") => {
        setLoading(true);
        try {
            const res = await getData(API_ENDPOINTS.getOwnerPayments, { search: q });
            setPayments(res.result?.transactions_history || []);
            setSummary(res.result);
        } catch (e) { console.error(e); }
        finally { setLoading(false); }
    };

    useEffect(() => { fetchPayments(); }, []);

    const totalEarnings      = Number(summary?.totalRevenue ?? 0);
    const totalPlatformRev   = Number(summary?.platformRevenue ?? 0);
    const totalCOD           = Number(summary?.totalCashOnDelivery ?? 0);
    const totalDebits        = Number(summary?.totalDebitsAmount ?? 0);
    const totalCredits       = Number(summary?.totalCreditAmount ?? 0);

    const filtered = useMemo(() => {
        if (typeFilter === "all") return payments;
        return payments.filter(p => p.transaction_type?.toLowerCase() === typeFilter);
    }, [payments, typeFilter]);

    const totalPages = Math.ceil(filtered.length / perPage);
    const paginated  = filtered.slice(page * perPage, (page + 1) * perPage);

    const handleSearch  = e => { if (e.key === "Enter") { fetchPayments(search.trim()); setPage(0); } };
    const handleClear   = () => { setSearch(""); fetchPayments(""); setPage(0); };
    const handleChange  = e => { setSearch(e.target.value); if (e.target.value === "") { fetchPayments(""); setPage(0); } };

    const creditCount = payments.filter(p => p.transaction_type?.toLowerCase() === "credit").length;
    const debitCount  = payments.filter(p => p.transaction_type?.toLowerCase() === "debit").length;

    return (
        <Box sx={{ bgcolor: T.bg, minHeight: "100vh", pb: 4 }}>

            {/* ── Hero header ── */}
            <Box sx={{
                background: `linear-gradient(135deg, ${T.teal} 0%, ${T.tealDk} 100%)`,
                borderRadius: "20px", p: 3, mb: 3,
                boxShadow: "0 4px 20px rgba(0,124,132,0.2)",
            }}>
                <Typography sx={{ fontSize: 22, fontWeight: 800, color: "#fff", fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                    Payments & Payouts
                </Typography>
                <Typography sx={{ fontSize: 13, color: "rgba(255,255,255,0.75)", mt: 0.5 }}>
                    Owner payout history, credits, debits and revenue summary
                </Typography>
            </Box>

            {/* ── KPI tiles ── */}
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 3 }}>
                <KpiTile title="Total Earnings"        value={fmtK(totalEarnings)}    icon={<CurrencyRupeeRoundedIcon />}        accent={T.teal}    iconBg={T.tealLt}    iconColor={T.teal}   loading={loading} />
                <KpiTile title="Platform Revenue"      value={fmtK(totalPlatformRev)} icon={<AccountBalanceWalletRoundedIcon />} accent={T.indigo}  iconBg={T.indigoLt}  iconColor={T.indigo} loading={loading} />
                <KpiTile title="Cash on Delivery"      value={fmtK(totalCOD)}         icon={<PointOfSaleRoundedIcon />}          accent={T.amber}   iconBg={T.amberLt}   iconColor={T.amber}  loading={loading} />
                <KpiTile title="Total Debits (Paid)"   value={fmtK(totalDebits)}      icon={<TrendingDownRoundedIcon />}         accent={T.rose}    iconBg={T.roseLt}    iconColor={T.rose}   loading={loading} />
                <KpiTile title="Total Credits"         value={fmtK(totalCredits)}     icon={<TrendingUpRoundedIcon />}           accent={T.emerald} iconBg={T.emeraldLt} iconColor={T.emerald}loading={loading} />
            </Box>

            {/* ── Payment history ── */}
            <Card sx={{ borderRadius: "16px", border: `1px solid ${T.border}`, boxShadow: "0 1px 4px rgba(15,23,42,0.06)", overflow: "hidden" }}>
                {/* Table toolbar */}
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 2, px: 2.5, py: 2, borderBottom: `1px solid ${T.border}` }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                        <Typography sx={{ fontSize: 15, fontWeight: 700, color: T.text, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                            Transaction History
                        </Typography>
                        {!loading && (
                            <Box sx={{ px: "10px", py: "2px", bgcolor: T.tealLt, borderRadius: "7px", fontSize: 12, fontWeight: 700, color: T.teal }}>
                                {payments.length}
                            </Box>
                        )}
                    </Box>

                    <Box sx={{ display: "flex", gap: 1.5, alignItems: "center", flexWrap: "wrap" }}>
                        {/* Type filter pills */}
                        <Box sx={{ display: "flex", gap: 0.75, bgcolor: T.bg, border: `1px solid ${T.border}`, borderRadius: "10px", p: 0.5 }}>
                            <FilterPill label="All"    active={typeFilter === "all"}    color={T.teal}    bg={T.tealLt}    onClick={() => { setTypeFilter("all");    setPage(0); }} />
                            <FilterPill label={`Credits (${creditCount})`} active={typeFilter === "credit"} color={T.emerald} bg={T.emeraldLt} onClick={() => { setTypeFilter("credit"); setPage(0); }} />
                            <FilterPill label={`Debits (${debitCount})`}  active={typeFilter === "debit"}  color={T.rose}    bg={T.roseLt}    onClick={() => { setTypeFilter("debit");  setPage(0); }} />
                        </Box>

                        {/* Search */}
                        <TextField
                            placeholder="Search trip, owner…"
                            size="small" value={search}
                            onChange={handleChange} onKeyDown={handleSearch}
                            sx={{ width: 220 }}
                            InputProps={{
                                startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 16, color: T.muted }} /></InputAdornment>,
                                endAdornment: search ? <InputAdornment position="end"><IconButton size="small" onClick={handleClear}><CloseIcon sx={{ fontSize: 14 }} /></IconButton></InputAdornment> : null,
                                sx: { borderRadius: "12px", bgcolor: "white", fontSize: 13 },
                            }}
                        />
                    </Box>
                </Box>

                {/* Table */}
                <TableContainer>
                    <Table size="small">
                        <TableHead>
                            <TableRow sx={{ bgcolor: T.bg }}>
                                {["Trip / Ref", "Owner", "Date", "Amount", "TDS", "Net Paid", "Type", "Method"].map(h => (
                                    <TableCell key={h} sx={{ fontWeight: 700, fontSize: 11, color: T.muted, textTransform: "uppercase", letterSpacing: "0.4px", py: 1.5, borderBottom: `2px solid ${T.border}` }}>{h}</TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading
                                ? Array.from({ length: 10 }).map((_, i) => <SkelRow key={i} />)
                                : paginated.length === 0
                                ? (
                                    <TableRow>
                                        <TableCell colSpan={8} align="center" sx={{ py: 6 }}>
                                            <Typography fontSize={13} color="text.secondary">No payment history found.</Typography>
                                        </TableCell>
                                    </TableRow>
                                )
                                : paginated.map(row => {
                                    const isDebit  = row.transaction_type?.toLowerCase() === "debit";
                                    const tripCell = row.trip_id || row.withdrawal_request_ref || "—";
                                    const method   = row.bank_method || row.payment_method || "—";
                                    return (
                                        <TableRow key={row.id} hover sx={{ "&:last-child td": { border: 0 } }}>
                                            <TableCell>
                                                <Typography sx={{ fontSize: 12, fontWeight: 700, color: T.teal, fontFamily: "monospace" }}>{tripCell}</Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography sx={{ fontSize: 13, fontWeight: 600, color: T.text }}>{row.owner_name || "—"}</Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography sx={{ fontSize: 12, color: T.muted }}>{fmtDate(row.createdAt)}</Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography sx={{ fontSize: 13, fontWeight: 700, color: T.text }}>{fmtRs(row.total_amount)}</Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography sx={{ fontSize: 12, color: isDebit ? T.rose : T.muted }}>
                                                    {isDebit ? fmtRs(row.tds_amount) : "—"}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography sx={{ fontSize: 13, fontWeight: 700, color: isDebit ? T.emerald : T.text }}>
                                                    {isDebit ? fmtRs(row.net_amount) : fmtRs(row.payout_amount)}
                                                </Typography>
                                            </TableCell>
                                            <TableCell><TypeChip type={row.transaction_type} /></TableCell>
                                            <TableCell><MethodBadge method={method} /></TableCell>
                                        </TableRow>
                                    );
                                })
                            }
                        </TableBody>
                    </Table>
                </TableContainer>

                {/* Pagination */}
                {!loading && payments.length > 0 && (
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", px: 2.5, py: 1.5, borderTop: `1px solid ${T.border}` }}>
                        <Typography sx={{ fontSize: 13, color: T.muted }}>
                            Showing {Math.min(page * perPage + 1, filtered.length)}–{Math.min((page + 1) * perPage, filtered.length)} of {filtered.length}
                        </Typography>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <Select value={perPage} onChange={e => { setPerPage(Number(e.target.value)); setPage(0); }} size="small" sx={{ borderRadius: "10px", fontSize: 13 }}>
                                {[15, 25, 50].map(n => <MenuItem key={n} value={n}>{n} per page</MenuItem>)}
                            </Select>
                            <IconButton size="small" onClick={() => setPage(p => Math.max(p-1, 0))} disabled={page === 0}
                                sx={{ bgcolor: T.bg, border: `1px solid ${T.border}`, borderRadius: "8px" }}>
                                <KeyboardArrowLeft sx={{ fontSize: 18 }} />
                            </IconButton>
                            <Typography sx={{ fontSize: 13, fontWeight: 600, color: T.text, minWidth: 56, textAlign: "center" }}>
                                {page + 1} / {totalPages}
                            </Typography>
                            <IconButton size="small" onClick={() => setPage(p => Math.min(p+1, totalPages-1))} disabled={page >= totalPages-1}
                                sx={{ bgcolor: T.bg, border: `1px solid ${T.border}`, borderRadius: "8px" }}>
                                <KeyboardArrowRight sx={{ fontSize: 18 }} />
                            </IconButton>
                        </Box>
                    </Box>
                )}
            </Card>
        </Box>
    );
}
